// Hook: manages the active POS order draft, creating one lazily on first item add.
import { useState, useCallback, useRef, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiPost, apiPatch, apiDelete, apiGet } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth/store";
import { useWorkstationConfig } from "./use-workstation-config";

export interface OrderItem {
  id: string;
  orderId: string;
  variantId: string;
  quantity: number;
  unitPrice: string;
  discount: string;
  lineTotal: string;
  note: string;
  sortOrder: number;
  productName?: string | null;
  sku?: string | null;
  /** True when the variant is serial-tracked. Commit requires exactly quantity serials. */
  serialTracked?: boolean;
  serials?: Array<{ id: string; serialNo: string }>;
}

export interface Order {
  id: string;
  code: string;
  status: string;
  subtotal: string;
  discountAmount: string;
  couponAmount: string;
  couponCode?: string | null;
  total: string;
  paidAmount: string;
  debtAmount: string;
  paymentMethod?: string | null;
  cashierEmail?: string | null;
  orderType?: string;
  items?: OrderItem[];
  customerId?: string | null;
  createdAt: string;
  paidAt?: string | null;
  voidedAt?: string | null;
}

/** Thrown by createDraft / ensureDraft when no retail workstation is configured. */
export class WorkstationNotConfiguredError extends Error {
  constructor() {
    super("Chưa cấu hình kho bán — liên hệ admin");
    this.name = "WorkstationNotConfiguredError";
  }
}

export function usePosCart() {
  const [order, setOrder] = useState<Order | null>(null);
  // Desired order type for the NEXT draft created (before any item/draft exists).
  // Once a draft exists, the order's own order_type is authoritative.
  const [pendingOrderType, setPendingOrderType] = useState<string>("retail");
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);

  // Retail workstation config — reads from localStorage, no admin endpoint call.
  const retailConfig = useWorkstationConfig("retail");

  // In-memory cache: variantId → first imageUrl (persists for the session lifetime).
  const imageCache = useRef<Record<string, string>>({});

  const cacheVariantImage = useCallback((variantId: string, imageUrl?: string) => {
    if (imageUrl) imageCache.current[variantId] = imageUrl;
  }, []);

  // Track closed (hidden) draft IDs — persisted in localStorage so they survive refresh.
  const [hiddenDraftIds, setHiddenDraftIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("pos_hidden_drafts");
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch { return new Set(); }
  });

  // Poll for current cashier's draft orders (refreshed every 30s).
  const { data: rawDrafts = [] } = useQuery({
    queryKey: ["orders", "drafts", user?.id],
    queryFn: () =>
      apiGet<Order[]>("/v1/orders", {
        status: "draft",
        cashier_id: user?.id,
        limit: 10,
      }),
    enabled: !!user,
    refetchInterval: 30_000,
  });

  const drafts = useMemo(
    () => rawDrafts.filter((d) => !hiddenDraftIds.has(d.id)),
    [rawDrafts, hiddenDraftIds],
  );

  // Close (hide) a draft tab — hidden client-side only (no server delete for drafts).
  const closeDraft = useCallback((orderId: string) => {
    setHiddenDraftIds((prev) => {
      const next = new Set(prev).add(orderId);
      localStorage.setItem("pos_hidden_drafts", JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Create a new draft order and return it.
  // workstationId may be passed explicitly (wholesale path) or left undefined
  // (retail path, resolved from the configured retail workstation).
  // When the retail workstation is not configured and no explicit id is given,
  // throws WorkstationNotConfiguredError so callers can surface a clear message.
  const createDraft = useCallback(async (workstationId?: string, orderType?: string): Promise<Order> => {
    const body: Record<string, string> = { orderType: orderType ?? pendingOrderType };

    if (workstationId) {
      // Explicit id (e.g. wholesale path) always takes precedence.
      body.workstationId = workstationId;
    } else {
      // Retail path: use configured workstation. Block if not set.
      if (!retailConfig.isConfigured) {
        throw new WorkstationNotConfiguredError();
      }
      body.workstationId = retailConfig.workstationId!;
    }

    const o = await apiPost<Order>("/v1/orders", body);
    setOrder(o);
    qc.invalidateQueries({ queryKey: ["orders", "drafts"] });
    return o;
  }, [qc, pendingOrderType, retailConfig]);

  // Ensure a draft exists, creating one if needed. Returns order id.
  // Propagates WorkstationNotConfiguredError if retail workstation is not set.
  const ensureDraft = useCallback(async (): Promise<string> => {
    if (order) return order.id;
    const o = await createDraft(undefined, pendingOrderType);
    return o.id;
  }, [order, createDraft, pendingOrderType]);

  // Switch retail/wholesale. With an active draft this PATCHes the order (server
  // re-prices lines); without one it just records the choice for the next draft.
  const setOrderType = useCallback(async (orderType: string) => {
    if (!order) {
      setPendingOrderType(orderType);
      return;
    }
    const o = await apiPatch<Order>(`/v1/orders/${order.id}`, { orderType });
    setOrder(o);
  }, [order]);

  // Add a variant to the current order (auto-creates draft if needed).
  // orderId may be passed explicitly to target a freshly-created draft whose
  // setOrder() has not yet committed to React state — avoids a second draft
  // and a stale-closure mismatch during the serial-first scan chain.
  const addItem = useMutation({
    mutationFn: async ({ variantId, quantity, orderId }: { variantId: string; quantity?: number; orderId?: string }) => {
      const oid = orderId ?? (await ensureDraft());
      return apiPost<Order>(`/v1/orders/${oid}/items`, { variantId, quantity: quantity ?? 1 });
    },
    onSuccess: (o) => setOrder(o),
  });

  // Update a line item (qty / price / discount / serials / note).
  const updateItem = useMutation({
    mutationFn: async ({
      itemId,
      quantity,
      unitPrice,
      discount,
      serialIds,
      note,
      orderId,
    }: {
      itemId: string;
      quantity?: number;
      unitPrice?: string;
      discount?: string;
      serialIds?: string[];
      note?: string;
      orderId?: string;
    }) => {
      // Prefer an explicit orderId (e.g. the just-created draft from a serial
      // scan) over `order` state, which may be stale within the same tick.
      const oid = orderId ?? order?.id;
      if (!oid) throw new Error("no active order");
      return apiPatch<Order>(`/v1/orders/${oid}/items/${itemId}`, {
        quantity,
        unitPrice,
        discount,
        serialIds,
        note,
      });
    },
    onSuccess: (o) => setOrder(o),
  });

  // Remove a line item.
  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      if (!order) throw new Error("no active order");
      return apiDelete(`/v1/orders/${order.id}/items/${itemId}`).then(
        () => apiGet<Order>(`/v1/orders/${order.id}`),
      );
    },
    onSuccess: (o) => setOrder(o as Order),
  });

  // Patch order header (discount, customer, note).
  const patchHeader = useMutation({
    mutationFn: async (patch: { discountAmount?: string; customerId?: string; note?: string }) => {
      if (!order) throw new Error("no active order");
      return apiPatch<Order>(`/v1/orders/${order.id}`, patch);
    },
    onSuccess: (o) => setOrder(o),
  });

  // Commit the order (cash payment, optional coupon, optional reason for partial-pay).
  // onError is intentionally omitted here so callers can provide their own handler
  // (e.g. the payment panel shows a toast with translateApiError so the cashier
  // knows which serial is missing/sold and can fix before retrying).
  const commit = useMutation({
    mutationFn: async ({
      paymentMethod,
      tenderedAmount,
      couponCode,
      reason,
    }: {
      paymentMethod: string;
      tenderedAmount: number;
      couponCode?: string;
      reason?: string;
    }) => {
      if (!order) throw new Error("no active order");
      return apiPost<Order>(`/v1/orders/${order.id}/commit`, {
        paymentMethod,
        tenderedAmount,
        ...(couponCode ? { couponCode } : {}),
        ...(reason ? { reason } : {}),
      });
    },
    onSuccess: (o) => {
      setOrder(o);
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
    // No onError here: caller owns error display so they can show context-aware messages
    // (e.g. "cần 1 serial, đã gắn 0" prompts the cashier to scan, not just a generic alert).
  });

  // Hard-delete a draft order server-side (manager/admin only — enforced by BE).
  // Used by the POS tab close action for privileged users to truly purge an
  // abandoned cart instead of just hiding it client-side.
  const deleteDraft = useMutation({
    mutationFn: (orderId: string) => apiDelete(`/v1/orders/${orderId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", "drafts"] });
    },
  });

  // Resume an existing draft order by ID.
  const resumeDraft = useCallback(async (orderId: string) => {
    const o = await apiGet<Order>(`/v1/orders/${orderId}`);
    setOrder(o);
  }, []);

  // Clear cart (start new order). Resets the pending order type to retail.
  const clearCart = useCallback(() => {
    setOrder(null);
    setPendingOrderType("retail");
  }, []);

  // Active order type: the order's own value once a draft exists, else the pending choice.
  const orderType = order?.orderType ?? pendingOrderType;

  return {
    order,
    drafts,
    addItem,
    updateItem,
    removeItem,
    patchHeader,
    commit,
    createDraft,
    ensureDraft,
    closeDraft,
    deleteDraft,
    resumeDraft,
    clearCart,
    orderType,
    setOrderType,
    imageCache: imageCache.current,
    cacheVariantImage,
    /** Retail workstation config — used by pos-screen-page for badge + gating. */
    retailConfig,
  };
}
